import type {
  AutoRechargeChangeLogListItem,
  AutoRechargeChangeLogListParams,
  AutoRechargeLogListItem,
  AutoRechargeLogListParams,
  AutoRechargeRuleAccountListItem,
  AutoRechargeRuleAccountListParams,
  AutoRechargeRuleListItem,
  AutoRechargeRuleListParams,
  SaveAutoRechargeRuleParams,
  SaveAutoRechargeRulePayload,
} from './type/ads-auto-transaction'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import {
  autoTransactionExecResultRequestValueMap,
  normalizeAutoTransactionExecResult,
} from '#/dict/modules/system-config/auto-transaction-exec-result-values'
import { formatDateRange, formatDateRangeToIso } from '#/utils'

export const adsAutoTransactionApi = defineApi('operation', ({ prefix }) => {
  function getRuleListParams(
    params: PagingRequest<AutoRechargeRuleListParams>,
  ) {
    return {
      ruleName: params.condition?.ruleName,
      id: params.condition?.id,
      customerId: params.condition?.customerId
        ? Number(params.condition.customerId)
        : undefined,
      customerName: params.condition?.customerName,
      status: Number(params.condition?.status),
      accountId: params.condition?.accountId,
      ruleSource: params.condition?.ruleSource,
    }
  }

  function getRuleList(params: PagingRequest<AutoRechargeRuleListParams>) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getRuleListParams(params),
    }
    return requestClient
      .post(prefix('adsAutoTransaction/queryRules'), requestParams)
      .then((res) => ({
        items:
          res?.list?.map((item: AutoRechargeRuleListItem) => {
            return {
              id: item.id,
              ruleName: item.ruleName,
              ruleConditionOperator: item.ruleConditionOperator,
              ruleConditionValue: item.ruleConditionValue,
              effectiveStartTime: item.effectiveStartTime,
              effectiveEndTime: item.effectiveEndTime,
              notifiers: item.notifiers,
              status: item.status?.toString(),
              accountCount: item.accountCount,
              customerId: item.customerId,
              customerName: item.customerName,
              amount: item.amount,
              cooldownHours: String(item.cooldownHours ?? 0),
              dailyLimit: String(item.dailyLimit ?? 0),
              notifyIntervalHours: item.notifyIntervalHours?.toString(),
              createdBy: item.createdBy,
              updatedBy: item.updatedBy,
              updatedTime: item.updatedTime,
            }
          }) || [],
        total: res?.total || 0,
      }))
  }

  function saveRule(values: SaveAutoRechargeRulePayload) {
    const { start: effectiveStartTime, end: effectiveEndTime } =
      formatDateRangeToIso(values.effectiveTime)
    const ruleSource = values.ruleSource
    const payload: SaveAutoRechargeRuleParams = {
      id: values.id,
      ruleName: values.ruleName,
      ruleConditionOperator: values.relation,
      ruleConditionValue: Number(values.threshold),
      ruleSource,
      status: Number(values.status),
      effectiveStartTime,
      effectiveEndTime,
      accountIds: (values.accountIds || []).map(Number),
    }

    if (ruleSource === 1) {
      payload.amount = Number(values.amount)
      payload.cooldownHours = Number(values.cooldownHours)
      payload.dailyLimit = Number(values.dailyLimit)
      payload.customerId = Number(values.customerId)
      payload.remark = values.remark
    } else {
      payload.notifyIntervalHours = Number(values.notifyIntervalHours)
      payload.notifiers = values.notifiers || []
    }
    return requestClient.post(prefix('adsAutoTransaction/saveRule'), payload)
  }

  function getBalanceMonitorLogList(
    params: PagingRequest<AutoRechargeLogListParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: {
        ruleSource: 2,
        judgeTime: formatDateRange(params.condition?.judgeTime),
        feishuSent: Number(params.condition?.feishuSent),
        notifySkipReason: params.condition?.notifySkipReason,
      },
    }
    return requestClient
      .post(prefix('adsAutoTransaction/queryLogs'), requestParams)
      .then((res) => ({
        items:
          res?.list?.map((item: AutoRechargeLogListItem) => ({
            judgeTime: item.judgeTime,
            ruleName: item.ruleName,
            mediaPlatform: item.mediaPlatform,
            agency: item.agency,
            accountId: item.accountId,
            accountName: item.accountName,
            balanceAtHit: item.balanceAtHit,
            thresholdAmount: item.thresholdAmount,
            notifier: item.notifier,
            feishuSent: item.feishuSent?.toString(),
            notifySkipReason: item.notifySkipReason,
          })) || [],
        total: res?.total || 0,
      }))
  }

  function getAutoRechargeLogList(
    params: PagingRequest<AutoRechargeLogListParams>,
  ) {
    const normalizedExecResult = normalizeAutoTransactionExecResult(
      params.condition?.execResult,
    )
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: {
        ruleSource: 1,
        judgeTime: formatDateRange(params.condition?.judgeTime),
        customerId: Number(params.condition?.customerId) || undefined,
        customerName: params.condition?.customerName,
        mediaPlatform: params.condition?.mediaPlatform,
        agency: params.condition?.agency,
        accountId: params.condition?.accountId,
        accountName: params.condition?.accountName,
        execResult: normalizedExecResult
          ? autoTransactionExecResultRequestValueMap[normalizedExecResult]
          : params.condition?.execResult,
        skipReason: params.condition?.skipReason,
      },
    }
    return requestClient
      .post(prefix('adsAutoTransaction/queryLogs'), requestParams)
      .then((res) => ({
        items:
          res?.list?.map((item: AutoRechargeLogListItem) => ({
            judgeTime: item.judgeTime,
            customerId: item.customerId,
            customerName: item.customerName,
            ruleName: item.ruleName,
            mediaPlatform: item.mediaPlatform,
            agency: item.agency,
            accountId: item.accountId,
            accountName: item.accountName,
            balanceAtHit: item.balanceAtHit,
            thresholdAmount: item.thresholdAmount,
            amount: item.amount,
            cooldownHoursLabel: item.cooldownHoursLabel,
            dailyLimitLabel: item.dailyLimitLabel,
            dailySuccessCount: item.dailySuccessCount,
            transactionResult: item.transactionResult,
            skipReason: item.skipReason,
            transactionBizNo: item.transactionBizNo,
          })) || [],
        total: res?.total || 0,
      }))
  }

  function getRuleAccounts(
    params: PagingRequest<AutoRechargeRuleAccountListParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: {
        ruleId: params.condition?.ruleId,
        ruleSource: params.condition?.ruleSource,
        customerId: params.condition?.customerId,
        account: params.condition?.account,
        mediaPlatform: params.condition?.mediaPlatform,
      },
    }
    return requestClient
      .post(prefix('adsAutoTransaction/queryRuleAccounts'), requestParams)
      .then((res) => {
        const list = res?.list || res?.data?.list || []
        const total = res?.total ?? res?.data?.total ?? 0
        return {
          items: list.map((item: AutoRechargeRuleAccountListItem) => ({
            id: String(item.id ?? item.accountId),
            accountId: item.accountId,
            accountName: item.accountName,
            mediaPlatform: item.mediaPlatform,
            currency: item.currency,
            isAllocation: item.selectable ?? true,
            selectable: item.selectable ?? true,
            selected: item.selected,
            boundRuleId: item.boundRuleId,
            boundRuleName: item.boundRuleName,
            reason: item.disabledReason || '',
            disabledReason: item.disabledReason || '',
          })),
          total,
        }
      })
  }

  function getChangeLogList(
    params: PagingRequest<AutoRechargeChangeLogListParams>,
  ) {
    const requestParams = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: {
        operatedAt: formatDateRange(params.condition?.operatedAt),
        customerId: params.condition?.customerId
          ? Number(params.condition.customerId)
          : undefined,
        customerName: params.condition?.customerName,
        ruleName: params.condition?.ruleName,
        opType: params.condition?.opType,
        operator: params.condition?.operator,
      },
    }
    return requestClient
      .post(prefix('adsAutoTransaction/queryChangeLogs'), requestParams)
      .then((res) => {
        const list = res?.list || res?.data?.list || []
        const total = res?.total ?? res?.data?.total ?? 0
        return {
          items: list.map((item: AutoRechargeChangeLogListItem) => ({
            id: item.id,
            operatedAt: item.operatedAt,
            operator: item.operator,
            customerId: item.customerId,
            customerName: item.customerName,
            ruleId: item.ruleId,
            ruleName: item.ruleName,
            opType: item.opType,
            changeSummary: item.changeSummary,
            opSourceLabel: item.opSourceLabel,
            remark: item.remark,
          })),
          total,
        }
      })
  }

  return {
    getRuleList,
    getRuleListParams,
    saveRule,
    getBalanceMonitorLogList,
    getAutoRechargeLogList,
    getRuleAccounts,
    getChangeLogList,
  }
})
